import type { ReviewResult, ReviewSection } from "../types";
import { api } from "./api";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

export class ReviewApiError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = "ReviewApiError";
    this.statusCode = statusCode;
  }
}

function formatSectionSummary(section: ReviewSection): string {
  return section.diagnostic_explanation || "";
}

export async function submitReview(
  file: File,
  assignmentId: string,
  userId: string
): Promise<{ reviewId: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("assignment_id", assignmentId);
  formData.append("user_id", userId);

  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/reviews/submit`, {
      method: "POST",
      body: formData,
    });
  } catch {
    throw new ReviewApiError(
      "Unable to connect to review service. Please check your internet connection.",
      0
    );
  }

  if (!response.ok) {
    let errorMessage = `Submission failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.detail) errorMessage = errorData.detail;
    } catch {
      // use default message
    }
    throw new ReviewApiError(errorMessage, response.status);
  }

  const data = await response.json();
  return { reviewId: data.review_id };
}

export async function resubmitDocument(
  file: File,
  assignmentId: string,
  userId: string
): Promise<{ reviewId: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("assignment_id", assignmentId);
  formData.append("user_id", userId);

  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/reviews/resubmit`, {
      method: "POST",
      body: formData,
    });
  } catch {
    throw new ReviewApiError(
      "Unable to connect to review service. Please check your internet connection.",
      0
    );
  }

  if (!response.ok) {
    let errorMessage = `Resubmission failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.detail) errorMessage = errorData.detail;
    } catch {
      // use default message
    }
    throw new ReviewApiError(errorMessage, response.status);
  }

  const data = await response.json();
  return { reviewId: data.review_id };
}

const POLL_INTERVAL_MS = 2000;

export function pollReviewStatus(
  reviewId: string,
  onComplete: (result: ReviewResult) => void,
  onError: (message: string) => void
): () => void {
  let cancelled = false;

  const poll = async () => {
    if (cancelled) return;
    try {
      const status = await api.getReviewStatus(reviewId);
      if (cancelled) return;

      if (status.status === "ai_reviewed") {
        const review = await api.getReview(reviewId);
        const submission = await api.getSubmission(review.submission_id);
        if (!cancelled)
          onComplete(transformReviewToResult(review, submission.id, submission.file_path));
      } else if (status.status === "failed") {
        if (!cancelled) onError(status.error_message || "Review failed. Please try again.");
      } else {
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    } catch {
      setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  setTimeout(poll, POLL_INTERVAL_MS);

  return () => {
    cancelled = true;
  };
}

function transformReviewToResult(
  review: any,
  submissionId: string,
  filePath: string
): ReviewResult {
  const meta = review.sections?._meta as any;

  const sections: ReviewSection[] = review.sections
    ? Object.entries(review.sections)
        .filter(([key]) => key !== "_meta")
        .map(([key, s]: [string, any]) => ({
          name: key,
          score: s.score || 0,
          band: s.band || s.command_word_level,
          diagnostic_explanation: s.diagnostic_explanation || s.short_rationale,
          observable_features_detected: s.observable_features_detected || s.detected_behaviours,
          missing_features_for_next_band: s.missing_features_for_next_band || s.missing_behaviours,
        }))
    : [];

  return {
    isRelevant: meta?.is_relevant ?? true,
    relevanceExplanation: meta?.relevance_explanation ?? "",
    overallScore: review.final_score,
    sections,
    criteriaScores: sections.map((section, index) => ({
      criterionId: `criterion-${index}`,
      criterionName: section.name,
      score: section.score,
      comments: formatSectionSummary(section),
    })),
    timestamp: new Date(review.reviewed_at || review.created_at),
    fileName: filePath.split("/").pop() || "submission.pdf",
    submissionId,
  };
}
